import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { v4 as uuidv4 } from 'uuid';
import { AppButton } from 'components/AppButton';
import { PATHS } from 'config/paths';
import {
  getConsentGiven,
  LoginType,
  TrackingEvents,
  useTrackingDispatch
} from 'modules/analytics';
import { getTrans } from 'modules/i18n';

export const LoginButton = () => {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const location = useLocation();

  const dispatch = useTrackingDispatch();

  const getPostLoginRedirectUrl = () => {
    if (isAuthenticated) {
      return PATHS.root;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (location.state?.from as string) ?? PATHS.root;
  };

  const handleLogin = async (connection?: string) => {
    const loginType =
      connection === 'email'
        ? LoginType.PASSWORDLESS_EMAIL
        : LoginType.CREDENTIALS;

    const processId = uuidv4();
    dispatch({
      type: TrackingEvents.INIT_LOGIN,
      payload: {
        id: processId,
        loginType
      }
    });
    const consentGiven = getConsentGiven();

    /**
     * The `authorizationParams` object is used to store additional parameters
     * that are required for an Auth0 post-login action.
     */
    const authorizationParams: Record<string, string> = {
      consent_given: String(consentGiven)
    };

    if (connection) {
      authorizationParams.connection = connection;
    }

    authorizationParams.login_type = loginType;
    authorizationParams.login_uuid = processId;

    await loginWithRedirect({
      appState: {
        returnTo: getPostLoginRedirectUrl()
      },
      authorizationParams
    });
  };

  const handleLoginWithEmail = () => handleLogin();
  const handleLoginPasswordless = () => handleLogin('email');

  return (
    <>
      <AppButton
        id="login-button-passwordless"
        testId="login-button-passwordless"
        color="success"
        variant="contained"
        onClick={handleLoginPasswordless}
        fullWidth={true}
      >
        {getTrans('general.loginPasswordless')}
      </AppButton>
      <AppButton
        id="login-button-email"
        testId="login-button-email"
        color="success"
        variant="contained"
        onClick={handleLoginWithEmail}
        fullWidth={true}
      >
        {getTrans('general.loginWithEmail')}
      </AppButton>
    </>
  );
};
